import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  HeartPulse, Building2, Stethoscope, Video, Bed, ShieldCheck,
  Phone, FileText, MapPin, Clock, Star, Users, ArrowRight, CheckCircle2, BadgeCheck
} from "lucide-react";
import Section from "@/components/Section";
import { BRAND } from "@/constants/brand";

const services = [
  { icon: Stethoscope, title: "Home Healthcare", desc: "Doctors, nurses, physiotherapists, dieticians, medical technologists & ayahs at your doorstep." },
  { icon: Bed, title: "Hospital Bed Booking", desc: "SOS emergency and scheduled bed bookings with real-time availability across partner hospitals." },
  { icon: Video, title: "Video Consultations", desc: "Encrypted telemedicine with hospital and home-care doctors as per Telemedicine Guidelines, 2020." },
  { icon: Phone, title: "Masked Audio Calls", desc: "Speak with healthcare personnel without sharing your personal phone number." },
  { icon: FileText, title: "Health Records Vault", desc: "Securely store prescriptions, reports and scans — encrypted at rest with AES-256." },
  { icon: ShieldCheck, title: "Insurance Shortcut", desc: "Save your health insurance provider details for quick reference within the app." },
];

const threeTiers = [
  {
    icon: HeartPulse,
    title: "For Patients",
    color: "#3780E0",
    items: [
      "Book hospital beds (SOS / scheduled)",
      "Book home healthcare visits",
      "Video consult with doctors & dieticians",
      "Store health records securely",
      "Rate and review healthcare experiences",
    ],
    link: "/for-patients",
  },
  {
    icon: Building2,
    title: "For Hospitals",
    color: "#2563B3",
    items: [
      "Receive SOS & scheduled bed bookings",
      "Manage doctor/physio/dietician calendars",
      "In-person & video consultation bookings",
      "Verified hospital registration listing",
      "Direct billing handled by your hospital",
    ],
    link: "/for-hospitals",
  },
  {
    icon: Users,
    title: "For Healthcare Personnel",
    color: "#0F4C5C",
    items: [
      "Get bookings directly from patients",
      "Accept or reject visits at your discretion",
      "Receive payments via the platform",
      "Verified medical registration listing",
      "Build a profile through patient reviews",
    ],
    link: "/for-personnel",
  },
];

const howItWorks = [
  { n: "01", title: "Sign Up & Verify", desc: "Patients create a profile. Hospitals and personnel are verified before going live." },
  { n: "02", title: "Book or Consult", desc: "Choose home visits, hospital beds, or instant video / masked-audio consultations." },
  { n: "03", title: "Receive Care", desc: "Care arrives at your home or at the partner hospital — with secure data handling." },
  { n: "04", title: "Rate & Track", desc: "Rate your experience and access your records anytime from your secure vault." },
];

export default function Home() {
  return (
    <>
      {/* HERO */}
      <section className="hc-gradient-hero" data-testid="hero-section">
        <div className="hc-container py-14 md:py-24 grid lg:grid-cols-2 gap-10 items-center">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="hc-chip"><BadgeCheck className="w-3.5 h-3.5" /> Launching soon — backed by 2 years of pilot operations</div>
            <h1 className="mt-4 text-4xl md:text-6xl font-extrabold leading-[1.05] tracking-tight text-[#0F2A4A]">
              {BRAND.tagline}.<br />
              <span className="text-[#3780E0]">Hospitals, home-care &amp; teleconsults — in one app.</span>
            </h1>
            <p className="mt-5 text-lg text-slate-600 max-w-xl">
              HomeCure by {BRAND.parent} connects patients with verified hospitals and healthcare
              personnel for home visits, bed bookings and video consultations — simply and securely.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/services" className="hc-btn-primary" data-testid="hero-cta-services">
                Explore Services <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/partner" className="hc-btn-outline" data-testid="hero-cta-partner">
                Partner with us
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-5 text-sm text-slate-600">
              <div className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-[#3780E0]" /> DPDP Act 2023 compliant</div>
              <div className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-[#3780E0]" /> Data stored in India</div>
              <div className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-[#3780E0]" /> Verified providers</div>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="absolute -inset-6 bg-gradient-to-tr from-[#C3D9F6] to-transparent rounded-3xl blur-2xl opacity-70" />
            <div className="relative rounded-3xl bg-white shadow-2xl border border-[#E8F0FB] p-6 md:p-8">
              <div className="flex items-center gap-4">
                <img src={BRAND.logoUrl} alt="HomeCure" className="w-14 h-14 object-contain" />
                <div>
                  <div className="text-2xl font-extrabold text-[#0F2A4A]">HomeCure App</div>
                  <div className="text-sm text-slate-500">Three sections — one ecosystem</div>
                </div>
              </div>
              <div className="mt-6 grid grid-cols-2 gap-3">
                {[
                  { icon: Bed, label: "SOS / Beds", c: "#3780E0" },
                  { icon: Stethoscope, label: "Home Care", c: "#2563B3" },
                  { icon: Video, label: "Video Consult", c: "#0F4C5C" },
                  { icon: FileText, label: "Health Vault", c: "#3780E0" },
                ].map((it, i) => (
                  <div key={i} className="rounded-xl bg-[#E8F0FB] border border-[#C3D9F6] p-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white" style={{ background: it.c }}>
                      <it.icon className="w-5 h-5" />
                    </div>
                    <div className="font-semibold text-[#0F2A4A]">{it.label}</div>
                  </div>
                ))}
              </div>
              <div className="mt-6 rounded-xl bg-[#C3D9F6] p-4 text-sm text-[#0F2A4A]">
                <div className="font-semibold mb-1">Pilot cities</div>
                <div className="flex flex-wrap gap-1.5">
                  {BRAND.cities.map((c) => (
                    <span key={c} className="px-2 py-0.5 rounded-full bg-white text-[#2563B3] text-xs font-semibold">{c}</span>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* STATS */}
      <div className="hc-container -mt-6 md:-mt-10 relative z-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 bg-white rounded-2xl shadow-xl border border-[#E8F0FB] p-5 md:p-6">
          {[
            { v: "2+", l: "Years of pilot operations" },
            { v: "3", l: "Pilot cities in West Bengal" },
            { v: "6", l: "Categories of personnel" },
            { v: "100%", l: "Verified hospital partners" },
          ].map((s, i) => (
            <div key={i} className="text-center px-2 py-3">
              <div className="text-2xl md:text-3xl font-extrabold text-[#3780E0]">{s.v}</div>
              <div className="text-xs md:text-sm text-slate-600 mt-1">{s.l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* THREE TIERS */}
      <Section
        eyebrow="Built for everyone"
        title="Three tiers — one connected platform"
        subtitle="HomeCure brings patients, hospitals and verified healthcare personnel together. We act as an intermediary platform that charges a small facilitation commission from patients for bookings."
        center
      >
        <div className="grid md:grid-cols-3 gap-5">
          {threeTiers.map((t, i) => (
            <div key={i} className="hc-card p-6 flex flex-col">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white" style={{ background: t.color }}>
                <t.icon className="w-6 h-6" />
              </div>
              <div className="mt-4 text-xl font-bold text-[#0F2A4A]">{t.title}</div>
              <ul className="mt-3 space-y-2 text-sm text-slate-600 flex-1">
                {t.items.map((it, idx) => (
                  <li key={idx} className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-[#3780E0] mt-0.5 shrink-0" /> <span>{it}</span></li>
                ))}
              </ul>
              <Link to={t.link} className="hc-link mt-5 inline-flex items-center gap-1 text-sm">
                Learn more <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ))}
        </div>
      </Section>

      {/* SERVICES */}
      <Section bg="light" eyebrow="What we offer" title="Services that meet you everywhere" center>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {services.map((s, i) => (
            <div key={i} className="hc-card p-6" data-testid={`service-card-${i}`}>
              <div className="w-11 h-11 rounded-lg bg-[#E8F0FB] flex items-center justify-center text-[#3780E0]">
                <s.icon className="w-5 h-5" />
              </div>
              <div className="mt-3 font-bold text-lg text-[#0F2A4A]">{s.title}</div>
              <div className="mt-1.5 text-sm text-slate-600">{s.desc}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* HOW IT WORKS */}
      <Section eyebrow="How it works" title="From booking to bedside in 4 steps" center>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {howItWorks.map((step, i) => (
            <div key={i} className="hc-card p-6">
              <div className="text-[#C3D9F6] text-5xl font-extrabold leading-none">{step.n}</div>
              <div className="mt-2 text-lg font-bold text-[#0F2A4A]">{step.title}</div>
              <div className="mt-1.5 text-sm text-slate-600">{step.desc}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* CITIES */}
      <Section bg="light" eyebrow="Currently serving" title="Live in 3 cities of West Bengal" subtitle="Our home healthcare services have been operating on a pilot basis (offline) for 2 years. The full app launches soon." center>
        <div className="grid sm:grid-cols-3 gap-5">
          {BRAND.cities.map((c) => (
            <div key={c} className="hc-card p-6 text-center">
              <MapPin className="w-7 h-7 text-[#3780E0] mx-auto" />
              <div className="mt-2 font-bold text-lg text-[#0F2A4A]">{c}</div>
              <div className="text-xs text-slate-500">West Bengal, India</div>
              <div className="hc-chip mt-3"><Clock className="w-3.5 h-3.5" /> Pilot operational</div>
            </div>
          ))}
        </div>
      </Section>

      {/* TRUST + COMPLIANCE */}
      <Section eyebrow="Privacy by design" title="Your data — protected, always" center>
        <div className="grid md:grid-cols-2 gap-6 items-start">
          <div className="hc-card p-6">
            <ShieldCheck className="w-9 h-9 text-[#3780E0]" />
            <div className="mt-3 text-xl font-bold text-[#0F2A4A]">Built for Indian regulations</div>
            <p className="mt-2 text-slate-600">
              We comply with the DPDP Act 2023, DPDP Rules 2025, IT Act 2000, SPDI Rules 2011 and the Telemedicine Practice Guidelines 2020. Health data is stored only in India.
            </p>
            <ul className="mt-4 space-y-2 text-sm text-slate-600">
              <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-[#3780E0] mt-0.5" /> AES-256 encryption at rest, TLS 1.3 in transit</li>
              <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-[#3780E0] mt-0.5" /> Patient data shared with hospitals only with explicit consent</li>
              <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-[#3780E0] mt-0.5" /> Masked audio calls — actual phone numbers never disclosed</li>
            </ul>
          </div>
          <div className="hc-card p-6 bg-[#E8F0FB]">
            <Star className="w-9 h-9 text-[#3780E0]" />
            <div className="mt-3 text-xl font-bold text-[#0F2A4A]">A truly transparent platform</div>
            <p className="mt-2 text-slate-700">
              HomeCure operates strictly as an intermediary under Section 2(1)(w) of the IT Act, 2000. We charge a clear facilitation commission from patients and have no influence over the final hospital bill, which is settled directly with the hospital.
            </p>
            <Link to="/privacy-policy" className="hc-link mt-5 inline-flex items-center gap-1 text-sm">
              Read full Privacy Policy <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </Section>

      {/* CTA */}
      <Section bg="primary" center>
        <h2 className="text-white text-3xl md:text-4xl font-extrabold">Be the first to know when we go live.</h2>
        <p className="text-white/90 mt-3 max-w-2xl mx-auto">Patients, hospitals and healthcare personnel — register your interest and our team will reach out at launch.</p>
        <div className="mt-7 flex justify-center gap-3 flex-wrap">
          <Link to="/contact" className="bg-white text-[#2563B3] hover:bg-slate-100 font-bold px-6 py-3 rounded-lg inline-flex items-center gap-2">Contact us <ArrowRight className="w-4 h-4" /></Link>
          <Link to="/partner" className="bg-[#0F2A4A] hover:bg-[#0B2240] text-white font-bold px-6 py-3 rounded-lg inline-flex items-center gap-2">Partner with us</Link>
        </div>
      </Section>
    </>
  );
}
